import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { getApiError } from '@/api/helpers/apiError'
import {
  deviceQueryKeys,
  useGetLatestClientAction,
  useTriggerEdgeClientAction
} from '@/queries/deviceQueries'
import { showToast } from '@/theme/notifications'
import {
  getEdgeClientErrorCategoryMessageId,
  getEdgeClientFallbackMessageId
} from './edgeClientErrorMessages'

type EdgeClientAction = 'update' | 'restart'

type TaskState = {
  action: EdgeClientAction | null
  isPolling: boolean
  logId: string | null
  hasTimedOut: boolean
}

const IDLE_TASK_STATE: TaskState = {
  action: null,
  isPolling: false,
  logId: null,
  hasTimedOut: false
}

const POLL_SAFETY_TIMEOUT_MS = 180_000

type UseEdgeClientActionsProps = {
  deviceId: string
  onActionSettled?: () => void
}

export const useEdgeClientActions = ({
  deviceId,
  onActionSettled
}: UseEdgeClientActionsProps) => {
  const intl = useIntl()
  const queryClient = useQueryClient()

  const [taskState, setTaskState] = useState<TaskState>(IDLE_TASK_STATE)
  const onActionSettledRef = useRef(onActionSettled)
  onActionSettledRef.current = onActionSettled

  const { mutateAsync, isPending } = useTriggerEdgeClientAction()

  const {
    data: latestAction,
    isFetching: isStatusFetching,
    error: latestActionError
  } = useGetLatestClientAction(deviceId, taskState.logId, taskState.isPolling)

  const isActionPending =
    !taskState.hasTimedOut &&
    (isPending ||
      isStatusFetching ||
      latestAction?.status === 'pending' ||
      latestAction?.status === 'running')

  const isUpdating = taskState.action === 'update' && isActionPending
  const isRestarting = taskState.action === 'restart' && isActionPending

  useEffect(() => {
    if (!taskState.isPolling) return

    if (latestActionError) {
      showToast(
        intl.formatMessage({ id: 'devices.edgeClient.statusCheckFailed' }),
        'error'
      )
      queryClient.removeQueries({
        queryKey: deviceQueryKeys.latestClientAction(deviceId)
      })
      onActionSettled?.()
      setTaskState(IDLE_TASK_STATE)
      return
    }

    if (latestAction?.status === 'success') {
      if (taskState.action === 'restart') {
        showToast(
          intl.formatMessage({ id: 'devices.edgeClient.restart.success' }),
          'success'
        )
      } else if (taskState.action === 'update') {
        showToast(
          intl.formatMessage({ id: 'devices.edgeClient.update.success' }),
          'success'
        )
      }

      onActionSettled?.()
      setTaskState(IDLE_TASK_STATE)
    }

    if (latestAction?.status === 'failed') {
      const categoryMessageId = getEdgeClientErrorCategoryMessageId(
        latestAction.error_category
      )

      const message = categoryMessageId
        ? intl.formatMessage({ id: categoryMessageId })
        : (() => {
            const fallback = intl.formatMessage({
              id: getEdgeClientFallbackMessageId(taskState.action)
            })

            return latestAction.error_detail
              ? `${fallback} (${latestAction.error_detail})`
              : fallback
          })()

      showToast(message, 'error')
      onActionSettled?.()
      setTaskState(IDLE_TASK_STATE)
    }
  }, [
    intl,
    latestAction,
    latestActionError,
    taskState.action,
    taskState.isPolling,
    onActionSettled,
    queryClient,
    deviceId
  ])

  useEffect(() => {
    if (!taskState.isPolling || taskState.hasTimedOut) return

    const timeoutId = window.setTimeout(() => {
      showToast(
        intl.formatMessage({ id: 'devices.edgeClient.pollTimedOut' }),
        'info'
      )
      onActionSettledRef.current?.()
      setTaskState((prev) => ({ ...prev, hasTimedOut: true }))
    }, POLL_SAFETY_TIMEOUT_MS)

    return () => window.clearTimeout(timeoutId)
  }, [taskState.isPolling, taskState.hasTimedOut, intl])

  const triggerAction = useCallback(
    async (action: EdgeClientAction) => {
      const previousPollingState = taskState.isPolling ? taskState : null

      queryClient.removeQueries({
        queryKey: deviceQueryKeys.latestClientAction(deviceId)
      })

      setTaskState({
        action,
        isPolling: false,
        logId: null,
        hasTimedOut: false
      })

      showToast(
        intl.formatMessage({
          id:
            action === 'restart'
              ? 'devices.edgeClient.restart.pending'
              : 'devices.edgeClient.update.pending'
        }),
        'info'
      )

      try {
        const { log_id: logId } = await mutateAsync({ deviceId, action })
        setTaskState({ action, isPolling: true, logId, hasTimedOut: false })
      } catch (err) {
        const { errorMessage } = getApiError(err)

        const message =
          errorMessage ||
          intl.formatMessage({ id: getEdgeClientFallbackMessageId(action) })

        showToast(message, 'error')

        if (previousPollingState) {
          setTaskState(previousPollingState)
        } else {
          onActionSettled?.()
          setTaskState(IDLE_TASK_STATE)
        }
      }
    },
    [deviceId, mutateAsync, intl, queryClient, onActionSettled, taskState]
  )

  const triggerUpdate = useCallback(
    () => triggerAction('update'),
    [triggerAction]
  )
  const triggerRestart = useCallback(
    () => triggerAction('restart'),
    [triggerAction]
  )

  return useMemo(
    () => ({
      triggerUpdate,
      triggerRestart,
      isUpdating,
      isRestarting
    }),
    [triggerUpdate, triggerRestart, isUpdating, isRestarting]
  )
}
